import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import urlJoin from 'url-join';
import type { Match } from 'react-router';

import adminPage from '@app/hocs/admin-page';
import templateGroupsReducer from '@app/reducers/entities/template-groups';
import { getExtensionsById } from '@app/reducers/entities/extensions';

import { T, t } from '@app/components/translation';
import { Table } from '@app/components/table';
import Button from '@app/components/button';
import AddIcon from '@app/components/icons/add';
import AddTemplateGroup from '@app/components/modals/add-template-group';
import ActionBar from '@app/components/action-bar';
import Toggle from '@app/components/toggle';
import Tooltip from '@app/components/tooltip';
import { ExtensionName } from '@app/components/extension/extension-name';
import TemplateGroupPage from './template-group';

import style from './template-groups.module.scss';
import { LocalizedDateTime } from '@app/components/localized-date-time';

export const QUERY_NAME = '/admin/account/extensions/template-groups';

export const TemplateGroupsComponent = ({ match }: Match) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      templateGroupsReducer.queryTemplateGroups({
        name: QUERY_NAME,
      }),
    );
  }, [
    dispatch,
  ]);

  const query = useSelector((state) =>
    templateGroupsReducer.getQuerySelector(state, {
      name: QUERY_NAME,
    }),
  );

  const templateGroups = useSelector((state) =>
    templateGroupsReducer.getTemplateGroupsSelector(state, {
      ids: query.result,
    }),
  );

  const extensionsById = useSelector(getExtensionsById);

  const handleStateChange = (templateGroup: Oneflow.TemplateGroup) => () => {
    const newTemplateGroupState = !templateGroup.active;

    dispatch(
      templateGroupsReducer.updateTemplateGroup({
        id: templateGroup.id,
        data: {
          active: newTemplateGroupState,
        },
      }),
    );
  };

  const getNameLink = (templateGroup: Oneflow.TemplateGroup) => {
    return <Link to={urlJoin(match.url, String(templateGroup.id))}>{templateGroup.name}</Link>;
  };

  const getTemplateGroupInfo = (templateGroup: Oneflow.TemplateGroup) => (
    <div className={style.TemplateGroup}>
      {getNameLink(templateGroup)}
      <div className={style.Description}>{templateGroup.description}</div>
    </div>
  );

  const getState = (templateGroup: Oneflow.TemplateGroup) => {
    if (templateGroup.usedBy) {
      return (
        <Tooltip
          message={
            <T
              _str="Not allowed to modify template groups created from other extensions"
              _comment="Tooltip showing reason for not being allowed to enable/disable template groups."
            />
          }
          side="top"
        >
          <Toggle
            version="latest"
            checked={templateGroup.active}
            disabled
          />
        </Tooltip>
      );
    }

    return (
      <Toggle
        version="latest"
        checked={templateGroup.active}
        onChange={handleStateChange(templateGroup)}
      />
    );
  };

  const getUsedByLink = (templateGroup: Oneflow.TemplateGroup) => {
    if (!templateGroup.usedBy) {
      return '-';
    }

    const extensionUsingTemplateGroup =
      extensionsById[templateGroup.usedBy as keyof typeof extensionsById];

    if (!extensionUsingTemplateGroup) {
      return '-';
    }

    return (
      <ExtensionName
        extension={extensionUsingTemplateGroup}
        isLinked
      />
    );
  };

  const getCreatedDate = (templateGroup: Oneflow.TemplateGroup) => (
    <LocalizedDateTime datetime={templateGroup.createdTime} />
  );

  const getTableConfig = () => {
    const infoColumn = {
      name: 'info',
      label: (
        <T
          _str="Template group"
          _comment="Column label in template group list"
        />
      ),
      type: 'cell',
      value: getTemplateGroupInfo,
    };
    const stateColumn = {
      name: 'state',
      label: (
        <T
          _str="Active"
          _comment="Column label in template group list"
        />
      ),
      type: 'cell',
      value: getState,
    };
    const usedByColumn = {
      name: 'usedBy',
      label: (
        <T
          _str="Used by"
          _comment="Column label in template group list"
        />
      ),
      type: 'cell',
      value: getUsedByLink,
    };

    const createdColumn = {
      name: 'createdTime',
      label: (
        <T
          _str="Created"
          _comment="Column label in template group list"
        />
      ),
      type: 'cell',
      value: getCreatedDate,
    };

    return {
      items: templateGroups,
      itemKey: 'id',
      actions: [],
      columns: [
        infoColumn,
        stateColumn,
        usedByColumn,
        createdColumn,
      ],
    };
  };

  return (
    <div>
      <ActionBar>
        <ActionBar.Group>
          <AddTemplateGroup>
            {(onClick: () => void) => (
              <Button
                data-testid="add-template-group"
                icon={AddIcon}
                kind="primary"
                onClick={onClick}
              >
                <T
                  _str="Add a template group"
                  _comment="Button text for adding template group on template group list page."
                />
              </Button>
            )}
          </AddTemplateGroup>
        </ActionBar.Group>
      </ActionBar>
      <Table
        config={getTableConfig()}
        query={query}
      />
    </div>
  );
};

export const propsMapper = () => ({
  modules: [
    [
      {
        path: '/:id(\\d+)',
        title: '',
        component: TemplateGroupPage,
      },
    ],
  ],
  section: (
    <T
      _str="Workflows"
      _comment="Used as the title for the section."
    />
  ),
  showWorkspace: true,
  title: t('Template groups', {
    _comment: 'Page title for the template groups page.',
  }),
});

export default adminPage(propsMapper)(TemplateGroupsComponent);
